import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TagInput from "@/components/ui/tag-input";

// Suggestion pools – derived from the ExerciseDB data we already have.
const BODY_PART_SUGGESTIONS = [
  "chest", "back", "shoulders", "upper arms", "lower arms", "upper legs", "lower legs", "waist", "cardio", "neck",
];
const EQUIPMENT_SUGGESTIONS = [
  "body weight", "barbell", "dumbbell", "cable", "kettlebell", "machine", "smith machine",
  "resistance band", "band", "medicine ball", "stability ball", "ez barbell", "trap bar",
  "rope", "weighted", "olympic barbell", "leverage machine", "bosu ball", "stationary bike",
];
const MUSCLE_SUGGESTIONS = [
  "pectorals", "triceps", "biceps", "deltoids", "lats", "trapezius", "rhomboids", "abs",
  "obliques", "forearms", "quadriceps", "hamstrings", "glutes", "calves", "hip flexors",
  "serratus anterior", "spine", "cardiovascular system", "upper back",
];

const empty = {
  name: "",
  gifUrl: "",
  exerciseId: "",
  difficulty: "",
  bodyParts: [],
  equipments: [],
  targetMuscles: [],
  secondaryMuscles: [],
  instructions: [],
};

// Accept records from either schema and coerce to the NEW one used by the form.
const fromInitial = (data) => {
  if (!data) return { ...empty };
  return {
    ...empty,
    ...data,
    name: data.name || "",
    gifUrl: data.gifUrl || data.gif_url || data.video_url || "",
    exerciseId: data.exerciseId || "",
    difficulty: (data.difficulty || "").toLowerCase(),
    bodyParts: Array.isArray(data.bodyParts)
      ? data.bodyParts
      : data.body_part
      ? [data.body_part]
      : data.category
      ? [data.category]
      : [],
    equipments: Array.isArray(data.equipments)
      ? data.equipments
      : data.equipment
      ? [data.equipment]
      : [],
    targetMuscles: Array.isArray(data.targetMuscles)
      ? data.targetMuscles
      : data.muscles_primary
      ? data.muscles_primary.split(/,\s*/).filter(Boolean)
      : [],
    secondaryMuscles: Array.isArray(data.secondaryMuscles)
      ? data.secondaryMuscles
      : data.muscles_secondary
      ? data.muscles_secondary.split(/,\s*/).filter(Boolean)
      : [],
    instructions: Array.isArray(data.instructions)
      ? data.instructions
      : data.description
      ? data.description.split(/\n+/).map((s) => s.trim()).filter(Boolean)
      : [],
  };
};

export default function ExerciseFormDialog({ open, onOpenChange, onSubmit, initialData }) {
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (open) setForm(fromInitial(initialData));
  }, [initialData, open]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {initialData ? "Edit Exercise" : "Add Exercise"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="exercise-form">
          {/* Preview row: gif + name */}
          <div className="flex items-start gap-4">
            {form.gifUrl ? (
              <img
                src={form.gifUrl}
                alt={form.name || "exercise"}
                className="w-28 h-28 rounded-lg object-cover bg-muted shrink-0"
              />
            ) : (
              <div className="w-28 h-28 rounded-lg bg-muted shrink-0" />
            )}
            <div className="flex-1 space-y-1">
              <Label>Exercise Name *</Label>
              <Input
                data-testid="exercise-form-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Barbell Bench Press"
                required
              />
              <Label className="pt-2">GIF / Image URL</Label>
              <Input
                data-testid="exercise-form-gif"
                value={form.gifUrl}
                onChange={(e) => set("gifUrl", e.target.value)}
                placeholder="https://static.exercisedb.dev/media/…"
                type="url"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Body Parts</Label>
              <TagInput
                value={form.bodyParts}
                onChange={(v) => set("bodyParts", v)}
                placeholder="e.g. chest"
                suggestions={BODY_PART_SUGGESTIONS}
                chipClassName="bg-blue-500/10 text-blue-400 border-blue-500/20"
              />
            </div>
            <div>
              <Label>Equipment</Label>
              <TagInput
                value={form.equipments}
                onChange={(v) => set("equipments", v)}
                placeholder="e.g. barbell"
                suggestions={EQUIPMENT_SUGGESTIONS}
                chipClassName="bg-amber-500/10 text-amber-400 border-amber-500/20"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>🎯 Target Muscles</Label>
              <TagInput
                value={form.targetMuscles}
                onChange={(v) => set("targetMuscles", v)}
                placeholder="e.g. pectorals"
                suggestions={MUSCLE_SUGGESTIONS}
                chipClassName="bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
              />
            </div>
            <div>
              <Label>Secondary Muscles</Label>
              <TagInput
                value={form.secondaryMuscles}
                onChange={(v) => set("secondaryMuscles", v)}
                placeholder="e.g. triceps"
                suggestions={MUSCLE_SUGGESTIONS}
                chipClassName="bg-purple-500/10 text-purple-400 border-purple-500/20"
              />
            </div>
          </div>

          <div>
            <Label>Difficulty</Label>
            <Select value={form.difficulty || ""} onValueChange={(v) => set("difficulty", v)}>
              <SelectTrigger data-testid="exercise-form-difficulty"><SelectValue placeholder="Select difficulty" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Instructions (one step per line)</Label>
            <Textarea
              data-testid="exercise-form-instructions"
              value={form.instructions.join("\n")}
              onChange={(e) =>
                set(
                  "instructions",
                  e.target.value
                    .split(/\n+/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                )
              }
              rows={6}
              placeholder={"Step 1: …\nStep 2: …"}
            />
          </div>

          <Button type="submit" className="w-full" data-testid="exercise-form-submit">
            {initialData ? "Save Changes" : "Add Exercise"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
